/**
 * API Utilities - PRODUCTION OPTIMIZED
 * 
 * Features:
 * - Retry logic for failed requests
 * - Better error handling
 * - Cache busting for dynamic endpoints
 * - Request timeout handling
 */

// Use environment variable or default to current domain
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Fetch with retry logic and timeout
 */
async function fetchWithRetry(url, options = {}, retries = 3, timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`🔄 Attempt ${attempt}/${retries}: ${url}`);
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        // If it's a duplicate submission, treat as success
        if (data.duplicate) {
          console.log('ℹ️ Duplicate submission detected, treating as success');
          return data;
        }
        
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`✅ Success on attempt ${attempt}`);
      return data;

    } catch (error) {
      clearTimeout(timeoutId);
      
      console.error(`❌ Attempt ${attempt} failed:`, error.message);

      // Don't retry on these errors
      if (error.name === 'AbortError') {
        throw new Error('Request timeout. Please check your connection and try again.');
      }

      if (error.message.includes('Invalid') || error.message.includes('404')) {
        throw error; // Don't retry validation errors
      }

      // If this was the last attempt, throw the error
      if (attempt === retries) {
        throw new Error(error.message || 'Network error. Please try again.');
      }

      // Wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`⏳ Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/**
 * Get invitation details
 */
export const getInvitation = async (code) => {
  try {
    // Add timestamp for cache busting on reload
    const timestamp = new Date().getTime();
    const url = `${API_BASE_URL}/get-invitation.php?code=${encodeURIComponent(code)}&_t=${timestamp}`;
    
    return await fetchWithRetry(url, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Error fetching invitation:', error);
    throw error;
  }
};

/**
 * Submit RSVP with retry logic
 */
export const submitRSVP = async (rsvpData) => {
  try {
    console.log('📤 Submitting RSVP:', rsvpData);
    
    const url = `${API_BASE_URL}/submit-rsvp.php`;
    
    // Use longer timeout for submission (15 seconds)
    return await fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify(rsvpData)
    }, 3, 15000);
    
  } catch (error) {
    console.error('Error submitting RSVP:', error);
    throw error;
  }
};

/**
 * Get all wishes
 */
export const getWishes = async () => {
  try {
    // Cache busting for wishes
    const timestamp = new Date().getTime();
    const url = `${API_BASE_URL}/get-wishes.php?_t=${timestamp}`;
    
    return await fetchWithRetry(url, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Error fetching wishes:', error);
    throw error;
  }
};

/**
 * Submit wish
 */
export const submitWish = async (wishData) => {
  try {
    const url = `${API_BASE_URL}/submit-wish.php`;
    
    return await fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify(wishData)
    });
  } catch (error) {
    console.error('Error submitting wish:', error);
    throw error;
  }
};

/**
 * Get statistics
 */
export const getStats = async () => {
  try {
    const url = `${API_BASE_URL}/get-stats.php`;
    
    return await fetchWithRetry(url, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
};

/**
 * Helper: Get invitation code from URL
 */
export const getCodeFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('code') || '';
};

/**
 * Helper: Format event type for display
 */
export const formatEventType = (eventType) => {
  const types = {
    'ceremony': 'Holy Matrimony',
    'reception': 'Reception',
    'both': 'Holy Matrimony & Reception'
  };
  return types[eventType] || eventType;
};
